"use client";

import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Clock, Video } from "lucide-react";
import {
  useGetCounselorCalenderQuery,
  useGetCounselorDateSlotsQuery,
} from "@/redux/features/publicCalender/publicCalender";
import { TSlot, SessionType } from "@/types";
import TimezoneSelector, {
  useSelectedTimezone,
} from "@/components/v3/booking/TimezoneSelector";
import {
  BUSINESS_TIMEZONE,
  convertTimeToTimezone,
  formatTimeWithTimezone,
  getTimezoneAbbreviation,
} from "@/lib/timezone";

export interface TimeSlot {
  time: string;
  available: boolean;
  slotId?: string; // Backend slot ID
}

export interface DateTimeSelection {
  date: Date | null;
  time: string | null;
  slotId?: string; // Backend slot ID for booking
}

interface BookingSummaryData {
  sessionType: "online" | "in-person";
  supportOption: string;
  duration: string;
  price: number;
  practitionerName?: string | null;
  practitionerRole?: string | null;
  practitionerImage?: string | null;
}

interface DateTimePickerProps {
  title?: string;
  subtitle?: string;
  availableDates?: Date[];
  timeSlots?: TimeSlot[];
  bookingSummary?: BookingSummaryData;
  defaultSelection?: DateTimeSelection;
  onSelectionChange?: (selection: DateTimeSelection) => void;
  counselorId?: string; // Backend counselor ID
  sessionType?: SessionType; // ONLINE or IN_PERSON
  travelMinutes?: number; // Travel time for in-person sessions
}

// Convert backend slot to frontend time slot
const backendSlotToTimeSlot = (slot: TSlot): TimeSlot => {
  // Handle both camelCase (startTime) and snake_case (start_time) from backend
  const startTime = slot.startTime || slot.start_time || "";

  // Extract time from startTime
  let time = startTime;

  // Handle 12-hour AM/PM format (e.g., "12:45 PM", "9:00 AM")
  const amPmMatch = startTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (amPmMatch) {
    let hours = parseInt(amPmMatch[1], 10);
    const minutes = amPmMatch[2];
    const meridiem = amPmMatch[3].toUpperCase();

    // Convert to 24-hour format
    if (meridiem === "PM" && hours !== 12) {
      hours += 12;
    } else if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }

    time = `${hours.toString().padStart(2, "0")}:${minutes}`;
  } else if (startTime.includes("T")) {
    // ISO format
    time = new Date(startTime).toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } else if (startTime.includes(":")) {
    // "HH:MM:SS" or "HH:MM" format - take first 5 chars
    time = startTime.substring(0, 5);
  }

  return {
    time,
    available: slot.status === "AVAILABLE",
    slotId: slot.id,
  };
};

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Format time (24-hour or already-12-hour) to 12-hour AM/PM format
const formatTimeTo12Hour = (time: string): string => {
  if (!time) return "";
  // If the string already carries an AM/PM meridiem, preserve it as-is
  // instead of re-deriving it from the raw hour (which breaks PM times
  // like "4:30 PM" by misreading them as AM).
  const amPmMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (amPmMatch) {
    const hours = parseInt(amPmMatch[1], 10);
    const minutes = amPmMatch[2];
    const meridiem = amPmMatch[3].toUpperCase();
    return `${hours}:${minutes} ${meridiem}`;
  }

  const match = time.match(/(\d{1,2}):(\d{2})/);
  if (!match) return time;
  const hours = parseInt(match[1]);
  const minutes = match[2];
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Default time slots (30-minute intervals from 12:00 to 17:30)
const defaultTimeSlots: TimeSlot[] = [
  { time: "12:00", available: true },
  { time: "12:30", available: true },
  { time: "13:00", available: true },
  { time: "13:30", available: true },
  { time: "14:00", available: true },
  { time: "14:30", available: true },
  { time: "15:00", available: true },
  { time: "15:30", available: true },
  { time: "16:00", available: true },
  { time: "16:30", available: true },
  { time: "17:00", available: true },
  { time: "17:30", available: true },
];

const defaultBookingSummary: BookingSummaryData = {
  sessionType: "online",
  supportOption: "Family Session",
  duration: "60 min",
  price: 130,
};

// Helper to get days in month
const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

// Helper to get first day of month (0 = Sunday, 1 = Monday, etc.)
const getFirstDayOfMonth = (year: number, month: number) => {
  const day = new Date(year, month, 1).getDay();
  // Convert to Monday-based (0 = Monday)
  return day === 0 ? 6 : day - 1;
};

// Check if two dates are the same day
const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

// Calendar Component
const Calendar = ({
  selectedDate,
  onDateSelect,
  availableDates,
}: {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  availableDates: Date[];
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

  const isDateAvailable = (date: Date) => {
    return availableDates.some((d) => isSameDay(d, date));
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const days: (number | null)[] = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [daysInMonth, firstDayOfMonth]);

  // Group days by week
  const weeks = useMemo(() => {
    const result: (number | null)[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <h3 className="font-onest text-xl font-semibold leading-none text-black md:text-[26px]">
        Select a Date & Time
      </h3>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="flex size-10 items-center justify-center rounded-full border border-v3-primary/30 transition-colors hover:bg-v3-primary/5 md:size-[50px]"
          aria-label="Previous month"
        >
          <ChevronLeft className="size-5 text-v3-primary md:size-6" />
        </button>
        <span className="font-onest text-base font-normal text-black md:text-xl">
          {MONTHS[currentMonth]} {currentYear}
        </span>
        <button
          type="button"
          onClick={handleNextMonth}
          className="flex size-10 items-center justify-center rounded-full border border-v3-primary/30 bg-v3-primary transition-colors hover:bg-v3-primary/90 md:size-[50px]"
          aria-label="Next month"
        >
          <ChevronRight className="size-5 text-white md:size-6" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center font-onest text-sm font-normal uppercase text-black"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex flex-col gap-2">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-2">
            {week.map((day, dayIndex) => {
              if (day === null) {
                return <div key={dayIndex} className="size-10 md:size-[57px]" />;
              }

              const date = new Date(currentYear, currentMonth, day);
              const isSelected = !!(selectedDate && isSameDay(date, selectedDate));
              const isAvailable = isDateAvailable(date);
              const isPast = date < new Date(today.setHours(0, 0, 0, 0));

              return (
                <button
                  key={dayIndex}
                  type="button"
                  onClick={() => isAvailable && !isPast && onDateSelect(date)}
                  disabled={!isAvailable || isPast}
                  className={`
                    flex size-10 items-center justify-center rounded-full
                    font-onest text-base transition-all duration-200 md:size-[57px] md:text-xl
                    ${
                      isSelected
                        ? "bg-v3-primary font-bold text-white"
                        : isAvailable && !isPast
                          ? "bg-v3-primary/10 font-bold text-v3-primary hover:bg-v3-primary/20"
                          : "font-normal text-black/60"
                    }
                    ${!isAvailable || isPast ? "cursor-not-allowed" : "cursor-pointer"}
                  `}
                  aria-label={`${day} ${MONTHS[currentMonth]} ${currentYear}`}
                  aria-pressed={isSelected}
                >
                  {day}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Time Zone Selector */}
      <div className="pt-4">
        <TimezoneSelector />
      </div>
    </div>
  );
};

// Time Slots Component
const TimeSlots = ({
  selectedDate,
  selectedTime,
  timeSlots,
  onTimeSelect,
  effectiveTimezone,
  isLocalTimezone,
}: {
  selectedDate: Date | null;
  selectedTime: string | null;
  timeSlots: TimeSlot[];
  onTimeSelect: (time: string, slotId?: string) => void;
  effectiveTimezone: string;
  isLocalTimezone: boolean;
}) => {
  if (!selectedDate) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-center font-onest text-lg text-black/60">
          Select a date to view available times
        </p>
      </div>
    );
  }

  const dayName = selectedDate.toLocaleDateString("en-US", { weekday: "long" });
  const monthName = selectedDate.toLocaleDateString("en-US", { month: "long" });
  const day = selectedDate.getDate();

  if (timeSlots.length === 0) {
    return (
      <div className="flex h-full w-full flex-col gap-6">
        <h3 className="shrink-0 font-onest text-xl font-normal text-black">
          {dayName}, {monthName} {day}
        </h3>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-center font-onest text-lg text-black/60">
            No available times for this date
          </p>
        </div>
      </div>
    );
  }

  // Helper to format time in the selected timezone
  const formatSlotTime = (time: string): string => {
    if (!selectedDate || !isLocalTimezone) {
      return formatTimeTo12Hour(time);
    }
    // Convert from business timezone to user's local timezone
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const convertedTime = convertTimeToTimezone(
      time,
      dateStr,
      BUSINESS_TIMEZONE,
      effectiveTimezone
    );
    return formatTimeTo12Hour(convertedTime);
  };

  const tzAbbrev = getTimezoneAbbreviation(effectiveTimezone);

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex shrink-0 flex-col">
        <h3 className="font-onest text-xl font-normal text-black">
          {dayName}, {monthName} {day}
        </h3>
        {isLocalTimezone && (
          <span className="font-onest text-xs text-black/50">
            Times shown in {tzAbbrev}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto pr-2">
        {timeSlots.map((slot) => (
          <button
            key={slot.slotId || slot.time}
            type="button"
            onClick={() =>
              slot.available && onTimeSelect(slot.time, slot.slotId)
            }
            disabled={!slot.available}
            className={`
              flex w-full shrink-0 items-center justify-center rounded-[10px] border px-7 py-3.5
              font-onest text-lg font-bold transition-all duration-200
              ${
                selectedTime === slot.time
                  ? "border-v3-primary bg-v3-primary text-white"
                  : slot.available
                    ? "border-v3-primary/60 text-v3-primary hover:bg-v3-primary/5"
                    : "cursor-not-allowed border-gray-300 text-gray-400"
              }
            `}
            aria-label={`Select time ${formatSlotTime(slot.time)}`}
            aria-pressed={selectedTime === slot.time}
          >
            {formatSlotTime(slot.time)}
          </button>
        ))}
      </div>
    </div>
  );
};

// Booking Summary Component
const BookingSummary = ({
  summary,
  selectedDate,
  selectedTime,
  effectiveTimezone,
  isLocalTimezone,
}: {
  summary: BookingSummaryData;
  selectedDate: Date | null;
  selectedTime: string | null;
  effectiveTimezone: string;
  isLocalTimezone: boolean;
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!selectedDate || !isLocalTimezone) {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    }
    // Convert from business timezone to user's local timezone
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const convertedTime = convertTimeToTimezone(
      time,
      dateStr,
      BUSINESS_TIMEZONE,
      effectiveTimezone
    );
    return formatTimeTo12Hour(convertedTime);
  };

  const userTzAbbrev = getTimezoneAbbreviation(effectiveTimezone);
  const businessTzAbbrev = getTimezoneAbbreviation(BUSINESS_TIMEZONE);

  return (
    <div className="flex flex-col">
      <h3 className="p-2.5 font-onest text-2xl font-semibold text-black">
        Booking Summary
      </h3>

      {/* Session Type Badge */}
      <div className="p-2.5">
        <div
          className="inline-flex items-center gap-2.5 rounded-[20px] border-4 border-white bg-v3-primary px-5 py-2.5"
          style={{
            boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
          }}
        >
          <Video className="size-6 text-white" />
          <span className="font-onest font-medium text-white">
            {summary.sessionType === "online"
              ? "Online Session"
              : "In-Person Session"}
          </span>
        </div>
      </div>

      {/* Support Option */}
      <div className="p-2.5">
        <div className="border-l-2 border-v3-primary pl-2.5">
          <div className="p-2.5">
            <span className="font-onest text-base font-semibold text-black">
              {summary.supportOption}
            </span>
          </div>
          <div className="flex items-center gap-7 p-2.5">
            <div className="flex items-center gap-1.5">
              <Clock className="size-6 text-v3-primary" />
              <span className="font-onest text-xl font-medium text-v3-primary">
                {summary.duration}
              </span>
            </div>
            <span className="font-onest text-xl font-bold text-black/60">
              ${summary.price}
            </span>
          </div>
        </div>
      </div>

      {/* Appointment Details */}
      <div className="rounded-[10px] bg-v3-primary/10 p-2.5">
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Appointment
          </span>
        </div>
        <div className="flex gap-2.5 px-2.5">
          <span className="font-onest text-base font-normal text-black/60">
            {selectedDate ? formatDate(selectedDate) : "Select a date"}
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            {selectedTime ? formatTime(selectedTime) : "Select a time"}
          </span>
        </div>
        <div className="px-2.5 pb-2.5">
          <span className="font-onest text-xs font-normal text-black/30">
            {isLocalTimezone
              ? `Your time (${userTzAbbrev})`
              : `Australia/Sydney (${businessTzAbbrev})`}
          </span>
        </div>
      </div>

      {/* Total Cost */}
      <div className="border-b border-black/20 p-2.5">
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Total Cost
          </span>
        </div>
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Session Fee
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${summary.price} AUD
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="p-2.5">
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Total
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${summary.price} AUD
          </span>
        </div>
      </div>
    </div>
  );
};

// Generate available dates (next 30 days with some random availability)
const generateAvailableDates = (): Date[] => {
  const dates: Date[] = [];
  const today = new Date();

  for (let i = 1; i <= 30; i++) {
    // Make some dates available (roughly every 2-3 days)
    if (i % 2 === 0 || i % 3 === 0) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
  }

  return dates;
};

// Main Component
const DateTimePicker = ({
  title = "Choose Date & Time",
  subtitle,
  availableDates: propAvailableDates,
  timeSlots: propTimeSlots,
  bookingSummary = defaultBookingSummary,
  defaultSelection,
  onSelectionChange,
  counselorId,
  sessionType,
  travelMinutes,
}: DateTimePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultSelection?.date || null
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(
    defaultSelection?.time || null
  );
  const [, setSelectedSlotId] = useState<string | undefined>(
    defaultSelection?.slotId
  );

  // Get timezone state
  const { effectiveTimezone, isLocalTimezone } = useSelectedTimezone();

  // Dynamic subtitle based on timezone
  const displaySubtitle = useMemo(() => {
    if (subtitle) return subtitle;
    const tzAbbrev = getTimezoneAbbreviation(effectiveTimezone);
    if (isLocalTimezone) {
      return `Times shown in your local timezone (${tzAbbrev})`;
    }
    return `All times displayed in Australian Eastern Time (${tzAbbrev})`;
  }, [subtitle, effectiveTimezone, isLocalTimezone]);

  // Fetch counselor calendar (available dates)
  const { data: calendarResponse, isLoading: calendarLoading } =
    useGetCounselorCalenderQuery(counselorId || "", {
      skip: !counselorId,
    });

  // Format selected date for slots query (use local date, not UTC to avoid timezone shift)
  const formattedDate = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : "";

  // Fetch time slots for selected date
  const { data: slotsResponse, isLoading: slotsLoading } =
    useGetCounselorDateSlotsQuery(
      {
        counselorId: counselorId || "",
        date: formattedDate,
        type: sessionType || "ONLINE",
        ...(travelMinutes != null && { travel_minutes: travelMinutes }),
      },
      {
        skip: !counselorId || !selectedDate,
      }
    );

  // Convert API calendar to available dates
  const availableDates: Date[] = useMemo(() => {
    if (propAvailableDates && propAvailableDates.length > 0) {
      return propAvailableDates;
    }

    if (calendarResponse?.data) {
      // Calendar response contains dates with available slots
      const data = calendarResponse.data;
      let calendarData: { date: string }[] = [];

      if (Array.isArray(data)) {
        calendarData = data;
      } else if (data && typeof data === "object") {
        // Handle nested structures
        if (Array.isArray((data as { dates?: { date: string }[] }).dates)) {
          calendarData = (data as { dates: { date: string }[] }).dates;
        } else if (Array.isArray((data as { data?: { date: string }[] }).data)) {
          calendarData = (data as { data: { date: string }[] }).data;
        } else if (Array.isArray((data as { calendar?: { date: string }[] }).calendar)) {
          calendarData = (data as { calendar: { date: string }[] }).calendar;
        }
      }

      if (calendarData.length > 0) {
        return calendarData
          .filter((cal) => cal.date)
          .map((cal) => {
            // Parse date string as local date (not UTC) to avoid timezone issues
            const [year, month, day] = cal.date.split('-').map(Number);
            return new Date(year, month - 1, day);
          });
      }
    }

    return generateAvailableDates();
  }, [propAvailableDates, calendarResponse]);

  // Normalize time to HH:MM format for consistent comparison
  const normalizeTime = (time: string): string => {
    const match = time.match(/(\d{1,2}):(\d{2})/);
    if (!match) return time;
    const hours = parseInt(match[1]).toString().padStart(2, '0');
    const minutes = match[2];
    return `${hours}:${minutes}`;
  };

  // Convert API slots to time slots
  const timeSlots: TimeSlot[] = useMemo(() => {
    if (propTimeSlots && propTimeSlots.length > 0) {
      return propTimeSlots;
    }

    if (slotsResponse?.data) {
      // Handle different API response formats
      let slots: TSlot[] = [];
      const data = slotsResponse.data;

      if (Array.isArray(data)) {
        // Direct array of slots
        slots = data;
      } else if (data && typeof data === "object") {
        // Nested structure like { slots: [...] } or { data: [...] }
        if (Array.isArray((data as { slots?: TSlot[] }).slots)) {
          slots = (data as { slots: TSlot[] }).slots;
        } else if (Array.isArray((data as { data?: TSlot[] }).data)) {
          slots = (data as { data: TSlot[] }).data;
        }
      }

      if (slots.length > 0) {
        const filteredSlots = slots
          .filter((slot) => slot.status === "AVAILABLE")
          // Filter by session type to show only matching slots (ONLINE or IN_PERSON)
          .filter((slot) => !sessionType || slot.type === sessionType)
          .map(backendSlotToTimeSlot)
          .sort((a, b) => a.time.localeCompare(b.time));

        // Deduplicate by normalized time to handle format differences (e.g., "9:00" vs "09:00")
        const uniqueSlots = new Map<string, TimeSlot>();
        for (const slot of filteredSlots) {
          const normalizedTime = normalizeTime(slot.time);
          if (!uniqueSlots.has(normalizedTime)) {
            uniqueSlots.set(normalizedTime, slot);
          }
        }

        // Return deduplicated slots
        return Array.from(uniqueSlots.values());
      }

      // API returned empty slots for this date - show empty, not defaults
      return [];
    }

    // Only show defaults when no counselorId (demo/static mode)
    return counselorId ? [] : defaultTimeSlots;
  }, [propTimeSlots, slotsResponse, sessionType, counselorId]);

  // Reset time when date changes
  useEffect(() => {
    if (defaultSelection?.date) {
      setSelectedDate(defaultSelection.date);
    }
    if (defaultSelection?.time) {
      setSelectedTime(defaultSelection.time);
    }
    if (defaultSelection?.slotId) {
      setSelectedSlotId(defaultSelection.slotId);
    }
  }, [defaultSelection]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setSelectedSlotId(undefined);
    onSelectionChange?.({ date, time: null, slotId: undefined });
  };

  const handleTimeSelect = (time: string, slotId?: string) => {
    setSelectedTime(time);
    setSelectedSlotId(slotId);
    onSelectionChange?.({ date: selectedDate, time, slotId });
  };

  return (
    <div className="flex flex-col gap-10">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h2 className="font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
          {title}
        </h2>
        <p className="max-w-[391px] font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
          {displaySubtitle}
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-[30px]">
        {/* Calendar Section */}
        <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:w-auto">
          {calendarLoading ? (
            <div className="flex h-[400px] items-center justify-center">
              <div className="size-8 animate-spin rounded-full border-4 border-v3-primary border-t-transparent" />
            </div>
          ) : (
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              availableDates={availableDates}
            />
          )}
        </div>

        {/* Time Slots Section */}
        <div className="h-[400px] max-h-[400px] w-full overflow-hidden rounded-[10px] bg-white p-4 md:p-[30px] lg:h-[659px] lg:max-h-none lg:w-[340px] lg:shrink-0">
          {slotsLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="size-8 animate-spin rounded-full border-4 border-v3-primary border-t-transparent" />
            </div>
          ) : (
            <TimeSlots
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              timeSlots={timeSlots}
              onTimeSelect={handleTimeSelect}
              effectiveTimezone={effectiveTimezone}
              isLocalTimezone={isLocalTimezone}
            />
          )}
        </div>

        {/* Booking Summary Section */}
        <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:flex-1">
          <BookingSummary
            summary={bookingSummary}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            effectiveTimezone={effectiveTimezone}
            isLocalTimezone={isLocalTimezone}
          />
        </div>
      </div>
    </div>
  );
};

export default DateTimePicker;
